import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { format, isPast } from 'date-fns';
import LoadingScreen from '../../../collums-components/components/common/loadingScreen';
import { withStyles } from '@material-ui/core/styles';
import { useMediaQuery } from '@material-ui/core';
import { MOBILE } from '../../../constants/breakpoints';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';

import { auth as authSelector, isAuthenticated as isAuthenticatedSelector } from '../../../redux/selectors/auth';
import { setCourseContext, clearBookingInfo } from '../../../redux/actions/book-appointment';
import CoursesApi from '../../../api/coursesApi';

import { courseTable } from './styles';
import { getCurrentTimezonedDate } from '../../../collums-components/helpers/timezone';
import { calculateGrossPrice } from '../../../collums-constants/utils';

const Courses = ({ classes }) => {
    const [data, setData] = useState([]);
    const [isFetching, setIsFetching] = useState(true);

    const auth = useSelector(authSelector);
    const isAuthenticated = useSelector(isAuthenticatedSelector);
    const isMobile = useMediaQuery(`(max-width:${MOBILE}px)`);
    const history = useHistory();
    const dispatch = useDispatch();

    useEffect(() => {
        const fetchCourses = async () => {
            if (!isAuthenticated || !auth.user?.id) {
                return;
            }

            try {
                setIsFetching(true);
                const response = await CoursesApi.getCustomerCourses(auth.user.id);

                let coursesArray = response;

                if (response.data && Array.isArray(response.data)) {
                    coursesArray = response.data;
                } else if (!Array.isArray(response) && response.data) {
                    coursesArray = response.data;
                }

                if (Array.isArray(coursesArray)) {
                    const sortedCourses = coursesArray.sort((a, b) => {
                        const dateA = new Date(a.invoiceItem?.createdAt || a.createdAt || 0);
                        const dateB = new Date(b.invoiceItem?.createdAt || b.createdAt || 0);
                        return dateB - dateA;
                    });
                    setData(sortedCourses);
                } else {
                    setData([]);
                }
            } catch (error) {
                setData([]);
            } finally {
                setIsFetching(false);
            }
        };

        fetchCourses();
        const handleCourseBookingComplete = () => {
            fetchCourses();
        };

        window.addEventListener('courseBookingComplete', handleCourseBookingComplete);

        return () => {
            window.removeEventListener('courseBookingComplete', handleCourseBookingComplete);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.user?.id, isAuthenticated]);

    const formatCurrency = amount => {
        return new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP'
        }).format(amount || 0);
    };

    const getServiceStatus = course => {
        const mainService = {
            name: course.service?.name || course.course?.service?.[0]?.name || 'N/A',
            quantity: course.quantity || 1,
            redeemed: course.redeemed || 0,
            reserved: course.reserved || 0,
            price: course.course?.service?.[0]?.defaultGrossPrice || course.service?.defaultGrossPrice || 0,
            isMainService: true
        };

        const additionalServices =
            course.course?.additionalServices?.map(service => ({
                name: service.service?.name || 'Additional Service',
                quantity: service.quantity || 1,
                redeemed: service.redeemed || 0,
                reserved: service.reserved || 0,
                price: service.service?.defaultGrossPrice || 0,
                isMainService: false
            })) || [];

        return { mainService, additionalServices };
    };

    const flattenCourseData = () => {
        const flattenedData = [];

        data.forEach((course, courseIndex) => {
            const serviceStatus = getServiceStatus(course);

            let invItem;
            try {
                invItem = JSON.parse(JSON.stringify(course.invoiceItem));
            } catch (e) {
                invItem = { ...course.invoiceItem };
            }

            const grossPrice = calculateGrossPrice({
                tax: invItem.tax,
                netPrice: invItem.netPrice,
                quantity: invItem.quantity
            });

            course.invoiceItem = {
                ...invItem,
                grossPrice: grossPrice
            };

            flattenedData.push({
                ...course,
                serviceInfo: serviceStatus.mainService,
                courseIndex,
                serviceIndex: 0,
                isFirstServiceRow: true,
                totalServicesInCourse: 1 + serviceStatus.additionalServices.length
            });

            serviceStatus.additionalServices.forEach((service, serviceIdx) => {
                flattenedData.push({
                    ...course,
                    serviceInfo: service,
                    courseIndex,
                    serviceIndex: serviceIdx + 1,
                    isFirstServiceRow: false,
                    totalServicesInCourse: 1 + serviceStatus.additionalServices.length
                });
            });
        });

        return flattenedData;
    };

    const handleBookCourse = courseRow => {
        try {
            const service = courseRow.serviceInfo.isMainService
                ? courseRow.service || courseRow.course?.service?.[0]
                : courseRow.course?.additionalServices?.[courseRow.serviceIndex - 1]?.service;

            if (!service) {
                console.error('No service found for course booking');
                return;
            }

            const courseContext = {
                courseId: courseRow.id || courseRow._id,
                courseName: courseRow.course?.name || courseRow.name,
                serviceIndex: courseRow.serviceIndex,
                isMainService: courseRow.serviceInfo.isMainService,
                originalCourse: courseRow
            };

            dispatch(setCourseContext(courseContext));
            dispatch(clearBookingInfo());

            // Always use the course purchase location if available - skip availability checking
            let selectedLocationId = null;

            if (courseRow.purchaseLocation?.id) {
                selectedLocationId = courseRow.purchaseLocation.id;
            }
            const categoryId =
                (typeof service.category === 'string' ? service.category : service.category?.id) ||
                service.categoryId ||
                courseRow.category?.id ||
                courseRow.categoryId ||
                courseRow.course?.category?.id ||
                courseRow.course?.categoryId;

            const serviceId = service.id || service._id;

            if (!categoryId || !serviceId) {
                console.error('Missing required IDs for booking navigation:', { categoryId, serviceId });
                return;
            }

            if (selectedLocationId) {
                const url = `/step/${selectedLocationId}/${categoryId}/${serviceId}?course=true&courseServiceBooking=true`;
                history.push(url);
            } else {
                // Start from location selection step
                const url = `/step/location?preselectedCategory=${categoryId}&preselectedService=${serviceId}&course=true&courseServiceBooking=true`;
                history.push(url);
            }
        } catch (error) {
            console.error('Error booking a course derived service:', error);
        }
    };

    const renderContent = () => {
        const flattenedData = flattenCourseData();

        return flattenedData.map((courseRow, index) => {
            const { serviceInfo, isFirstServiceRow } = courseRow;

            return (
                <TableRow
                    key={`${courseRow.id || courseRow._id}-${courseRow.serviceIndex}-${index}`}
                    className={classes.row}
                    style={{
                        backgroundColor: courseRow.courseIndex % 2 === 0 ? '#efefef' : '#fff',
                        borderTop: isFirstServiceRow && courseRow.courseIndex > 0 ? '2px solid #ccc' : 'none'
                    }}
                >
                    <TableCell component="th" scope="row">
                        {isFirstServiceRow
                            ? courseRow.invoiceItem?.createdAt
                                ? format(getCurrentTimezonedDate(courseRow.invoiceItem.createdAt), 'dd/MM/yyyy HH:mm')
                                : courseRow.soldAt
                                ? format(getCurrentTimezonedDate(courseRow.soldAt), 'dd/MM/yyyy HH:mm')
                                : 'N/A'
                            : ''}
                    </TableCell>

                    <TableCell>{isFirstServiceRow ? courseRow.course?.name || courseRow.name || 'N/A' : ''}</TableCell>

                    <TableCell>
                        {isFirstServiceRow
                            ? (() => {
                                  const price =
                                      courseRow.invoiceItem?.grossPrice || courseRow.invoiceItem?.netPrice || 0;
                                  return formatCurrency(price);
                              })()
                            : ''}
                    </TableCell>

                    <TableCell>
                        <div>{serviceInfo.name}</div>
                    </TableCell>

                    <TableCell>{serviceInfo.quantity}</TableCell>
                    <TableCell>{serviceInfo.redeemed}</TableCell>
                    <TableCell>{serviceInfo.reserved}</TableCell>
                    <TableCell>{serviceInfo.quantity - serviceInfo.redeemed - serviceInfo.reserved}</TableCell>

                    <TableCell>
                        {isFirstServiceRow
                            ? courseRow.endsOn
                                ? format(getCurrentTimezonedDate(courseRow.endsOn), 'dd/MM/yyyy')
                                : 'No expiry'
                            : ''}
                    </TableCell>

                    <TableCell>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => handleBookCourse(courseRow)}
                            size="small"
                            disabled={
                                serviceInfo.quantity - serviceInfo.redeemed - serviceInfo.reserved <= 0 ||
                                (courseRow.endsOn && isPast(getCurrentTimezonedDate(courseRow.endsOn)))
                            }
                        >
                            Book
                        </Button>
                    </TableCell>
                </TableRow>
            );
        });
    };

    return (
        <>
            {isFetching && <LoadingScreen />}
            {isMobile && <div className={classes.popper}> Swipe table to see all columns </div>}
            <TableContainer component={Paper} className={classes.container} id="scroll-bottom">
                <Table className={classes.table} size="small" aria-label="customer courses">
                    <TableHead>
                        <TableRow id="table">
                            <TableCell>Date</TableCell>
                            <TableCell>Course</TableCell>
                            <TableCell>Course Price</TableCell>
                            <TableCell>Service</TableCell>
                            <TableCell>Quantity</TableCell>
                            <TableCell>Used</TableCell>
                            <TableCell>Booked</TableCell>
                            <TableCell>Available</TableCell>
                            <TableCell>Expiry date</TableCell>
                            <TableCell>Book</TableCell>
                        </TableRow>
                    </TableHead>
                    {!!data.length && <TableBody>{renderContent()}</TableBody>}
                </Table>
                {!data.length && (
                    <div className={classes.noContent}>
                        <p>No courses to show.</p>
                    </div>
                )}
            </TableContainer>
        </>
    );
};

export default withStyles(courseTable)(Courses);
